import React, { useEffect, useState, useContext, useCallback, useRef } from "react";
import axios from "axios";
import "./details.css";
import Spinner from "../Components/Spinner";
import Tooltip from "@mui/material/Tooltip";
import { useNavigate, useParams } from "react-router-dom";
import { BASE_URL_API } from "../Constants";

import { DeviceIcon } from "../Icons";
import {
  DeleteIcon as SvgDeleteIcon,
  HistoryIcon as SvgHistoryIcon,
  VerticalDividerIcon,
} from "../svg/UtilsIcons";

import { useAuth } from "../hooks/AuthProvider";
import { deleteAssetById } from "../lib/actions";

import { LanguageContext } from "../context/LanguageContext";
import assetTranslations from "../languages/asset.json";
import Alert from "../Components/Alert";
import AppDialog from "../Components/AppDialog"; 
import moment from "moment";
import { EditAssetCard } from "./EditAssetCard";
import {
  AssetCardMainIcon,
  EfficiencyIcon,
  CapacityIcon,
  IdentificationIcon,
  MaintenanceIcon,
  OperationalIcon,
  PowerIcon,
  VoltageIcon,
} from "../svg/AssetCardIcon";

export const AssetCardDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang, t } = useContext(LanguageContext);
  const assetT = assetTranslations[lang].assetDetails;
  const isRtl = ["ar", "ur"].includes(lang);

  const { permissions, org } = useAuth();
  const canViewAssets = permissions["view_assets"];
  const canEditAssets = permissions["edit_assets"];
  const canDeleteAssets = permissions["delete_assets"];

  const [alertState, setAlertState] = useState({
    open: false,
    message: "",
    type: "info",
    position: isRtl ? "left" : "right",
  });

  const showAlert = useCallback(
    (type, message) => {
      setAlertState({
        open: true,
        type,
        message,
        position: isRtl ? "left" : "right",
      });
    },
    [isRtl]
  );

  const resolverRef = useRef(null);
  const [dialog, setDialog] = useState({
    open: false,
    type: "alert", // "alert" | "confirm"
    variant: "info", // "info" | "danger" | "error"
    icon: "alert", // "alert" | "delete"
    title: "",
    message: "",
    confirmText: "Ok",
    cancelText: "Cancel",
    showCancel: false,
  });

  const closeDialog = useCallback((result = false) => {
    setDialog((d) => ({ ...d, open: false }));
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  }, []);

  const alert = useCallback(
    (opts) =>
      new Promise((resolve) => {
        resolverRef.current = resolve;

        setDialog({
          open: true,
          type: opts?.type || "alert",
          variant: opts?.variant || "info",
          icon: opts?.icon || "alert",
          title: opts?.title || "",
          message: opts?.message || "",
          confirmText: opts?.confirmText || "Ok",
          cancelText: opts?.cancelText || "Cancel",
          showCancel: Boolean(opts?.showCancel),
        });
      }),
    []
  );

  const [details, setDetails] = useState({ data: null, loading: true });
  const [showEditAssetModal, setShowEditAssetModal] = useState(false);

  const fetchAssetDetails = useCallback(() => {
    if (!canViewAssets) {
      setDetails({ data: null, loading: false });
      return;
    }

    setDetails({ data: null, loading: true });

    if (!id || !Number.isInteger(Number(id)) || Number(id) < 1) {
      showAlert("error", assetT.invalidAssetId);
      setDetails({ data: null, loading: false });
      return;
    }
    if (!org?.id) {
      showAlert("error", "Organization ID not available.");
      setDetails({ data: null, loading: false });
      return;
    }

    axios
      .get(`${BASE_URL_API}/asset-card/${id}`, {
        params: { organization_id: org.id },
      })
      .then((res) => {
        if (res.data && res.data.status === "success") {
          setDetails({ data: res.data.data, loading: false });
        } else {
          showAlert("error", assetT.failedToRetrieve);
          setDetails({ data: null, loading: false });
        }
      })
      .catch((err) => {
        console.error("API Error:", err);
        const errorResponse = err.response;

        if (errorResponse) {
          if (errorResponse.status === 404) {
            showAlert("error", assetT.assetNotFound.replace("{id}", id));
          } else if (errorResponse.status === 403) {
            showAlert("error", "You do not have permission to view this asset.");
          } else if (
            errorResponse.status === 400 &&
            errorResponse.data &&
            errorResponse.data.errors
          ) {
            const errorMessage = errorResponse.data.errors.map((e) => e.msg).join(" ");
            showAlert("error", errorMessage);
          } else {
            showAlert("error", assetT.unexpectedError);
          }
        } else {
          showAlert("error", assetT.networkError);
        }
        setDetails({ data: null, loading: false });
      });
  }, [id, org?.id, canViewAssets, showAlert, assetT]);

  useEffect(() => {
    fetchAssetDetails();
  }, [fetchAssetDetails]);

  if (!canViewAssets) {
    return <div style={{ padding: "20px" }}>{assetT.noPermission}</div>;
  }

  const handleEditAssetClick = () => setShowEditAssetModal(true);
  const handleViewHistoryClick = () => navigate(`/asset-card/details/${id}/history`);

  const handleAssetDelete = () => {
    deleteAssetById(
      details.data.id,
      details.data.asset_name,
      org.id,
      () => navigate("/asset-card"),
      alert,
      () => t.actions
    );
  };

  const renderDetail = (title, value) => {
    if (value === null || value === undefined || value === "") return null;
    return (
      <div className="asset-detail-item">
        <div className="asset-detail-label">{title}</div>
        <div className="asset-detail-value">{value}</div>
      </div>
    );
  };

  const renderDateDetail = (title, dateValue) => {
    if (!dateValue) return null;
    const formattedDate = moment(dateValue).isValid()
      ? moment(dateValue).format("DD/MM/YYYY")
      : dateValue;
    return (
      <div className="asset-detail-item">
        <div className="asset-detail-label">{title}</div>
        <div className="asset-detail-value">{formattedDate}</div>
      </div>
    );
  };

  if (details.loading) return <Spinner />;

  if (!details.data?.id) {
    return <div className="asset-no-data">{assetT.noData || "No data to display."}</div>;
  }

  const idStepTitle = assetTranslations[lang].addAsset.step1.title;
  const op = details.data.OperationalData || {};
  const ml = details.data.MaintenanceLifecycle || {};
  const fo = details.data.FinancialOwnership || {};
  const iot = details.data.IotMonitoring || {};

  return (
    <div className="configuration asset-card-page" dir={isRtl ? "rtl" : "ltr"}>
      <Alert
        open={alertState.open}
        close={() => setAlertState({ ...alertState, open: false })}
        message={alertState.message}
        type={alertState.type}
        position={alertState.position}
      />

      <AppDialog
        open={dialog.open}
        type={dialog.type}
        variant={dialog.variant}
        icon={dialog.icon}
        title={dialog.title}
        message={dialog.message}
        confirmText={dialog.confirmText}
        cancelText={dialog.cancelText}
        showCancel={dialog.showCancel}
        onConfirm={() => closeDialog(true)}
        onCancel={() => closeDialog(false)}
        onClose={() => closeDialog(false)}
      />

      {showEditAssetModal && (
        <EditAssetCard
          assetId={details.data.id}
          setState={setShowEditAssetModal}
          refresh={fetchAssetDetails}
        />
      )}

      <div className="asset-top-bar">
        <button className="asset-back-btn" onClick={() => navigate("/asset-card")}>
          <span className="asset-back-icon">←</span>
          <span>{assetT.backLabel || "Back"}</span>
        </button>

        {canEditAssets && (
          <button className="asset-edit-main-btn" onClick={handleEditAssetClick}>
            {assetT.editAsset}
          </button>
        )}
      </div>

      <div className="asset-hero-card">
        <div className="asset-hero-left">
          <div className="asset-hero-icon">
            <AssetCardMainIcon />
          </div>

          <div className="asset-hero-text">
            <h1>{details.data.asset_name}</h1>

            <div className="details-inline-actions" style={{ marginTop: 10 }}>
              <Tooltip title={assetT.viewHistory || "View History"}>
                <button
                  type="button"
                  className="details-inline-action-btn"
                  onClick={handleViewHistoryClick}
                >
                  <SvgHistoryIcon />
                </button>
              </Tooltip>

              <span className="details-inline-divider">
                <VerticalDividerIcon />
              </span>

              {canDeleteAssets && (
                <Tooltip title={assetT.deleteAsset}>
                  <button
                    type="button"
                    className="details-inline-action-btn"
                    onClick={handleAssetDelete}
                  >
                    <SvgDeleteIcon color={"rgb(236, 69, 78)"} />
                  </button>
                </Tooltip>
              )}
            </div>

            <div className="asset-hero-meta">
              <span className="asset-code-pill">{details.data.asset_code}</span>

              <Tooltip title={assetT.tooltipDeviceName}>
                <div className="asset-device-pill">
                  <DeviceIcon color={"var(--accent)"} />
                  <span>{details.data.Device?.name || "N/A"}</span>
                </div>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>

      <div className="asset-main-panel">
        <div className="asset-main-grid">
          <section className="asset-section-card asset-section-identification">
            <header className="asset-section-header">
              <div className="asset-section-icon">
                <IdentificationIcon />
              </div>
              <div className="asset-section-title-block">
                <h2>{idStepTitle}</h2>
                <span>
                  {assetT.identificationSubtitle || "Identification & Classification"}
                </span>
              </div>
            </header>

            <div className="asset-details-grid">
              {renderDetail(assetT.fieldName, details.data.asset_name)}
              {renderDetail(assetT.fieldCode, details.data.asset_code)}
              {renderDetail(assetT.fieldCategorySubcategory, details.data.category_subcategory)}
              {renderDetail(assetT.fieldBrand, details.data.brand)}
              {renderDetail(assetT.fieldModelNumber, details.data.model_number)}
              {renderDetail(assetT.fieldSerialNumber, details.data.serial_number)}
              {renderDetail(assetT.fieldType, details.data.type)}
              {renderDetail(assetT.fieldManufacturer, details.data.manufacturer)}
              {renderDateDetail(assetT.fieldManufactureDate, details.data.manufacture_date)}
              {renderDetail(assetT.fieldLocation, details.data.location)}
            </div>
          </section>

          {details.data.OperationalData && (
            <section className="asset-section-card asset-section-operational">
              <header className="asset-section-header">
                <div className="asset-section-icon">
                  <OperationalIcon />
                </div>
                <div className="asset-section-title-block">
                  <h2>{assetT.operationalDataTitle}</h2>
                </div>
              </header>

              <div className="asset-kpi-grid">
                <div className="asset-kpi-card">
                  <div className="asset-kpi-header">
                    <div className="asset-section-sub-icon asset-section-sub-icon-power">
                    <PowerIcon />
                    </div>
                    <span>{assetT.fieldPower}</span>
                  </div>
                  <div className="asset-kpi-value">{op.power ?? "--"}</div>
                </div>

                <div className="asset-kpi-card">
                  <div className="asset-kpi-header">
                    <div className="asset-section-sub-icon asset-section-sub-icon-voltage">
                    <VoltageIcon />
                    </div>
                    <span>{assetT.fieldVoltage}</span>
                  </div>
                  <div className="asset-kpi-value">{op.voltage ?? "--"}</div>
                </div>

                <div className="asset-kpi-card">
                  <div className="asset-kpi-header">
                    <div className="asset-section-sub-icon asset-section-sub-icon-efficiency">
                    <EfficiencyIcon />
                    </div>
                    <span>{assetT.fieldEfficiencyPercent}</span>
                  </div>
                  <div className="asset-kpi-value">{op.efficiency_percent ?? "--"}</div>
                </div>

                <div className="asset-kpi-card">
                  <div className="asset-kpi-header">
                    <div className="asset-section-sub-icon asset-section-sub-icon-capacity">
                    <CapacityIcon />
                    </div>
                    <span>{assetT.fieldCapacityRating}</span>
                  </div>
                  <div className="asset-kpi-value">{op.capacity_rating ?? "--"}</div>
                </div>
              </div>

              <div className="asset-kpi-bottom-row">
                <div className="asset-kpi-bottom-item">
                  <span className="label">{assetT.fieldCurrent}</span>
                  <span className="value">{op.current ?? "--"}</span>
                </div>
                <div className="asset-kpi-bottom-item">
                  <span className="label">{assetT.fieldConnectionType}</span>
                  <span className="value">{op.connection_type ?? "--"}</span>
                </div>
                <div className="asset-kpi-bottom-item">
                  <span className="label">{assetT.fieldRunningHoursCounter}</span>
                  <span className="value">{op.running_hours_counter ?? "--"}</span>
                </div>
              </div>
            </section>
          )}

          {details.data.MaintenanceLifecycle && (
            <section className="asset-section-card asset-section-maintenance">
              <header className="asset-section-header">
                <div className="asset-section-icon">
                  <MaintenanceIcon />
                </div>
                <div className="asset-section-title-block">
                  <h2>{assetT.maintenanceLifecycleTitle}</h2>
                </div>
              </header>

              <div className="asset-details-grid">
                {renderDateDetail(assetT.fieldInstallationDate, ml.installation_date)}
                {renderDateDetail(assetT.fieldWarrantyExpiryDate, ml.warranty_expiry_date)}
                {renderDetail(assetT.fieldConditionStatus, ml.condition_status)}
                {renderDetail(assetT.fieldTechnicalEvaluation, ml.technical_evaluation)}
                {renderDetail(assetT.fieldCriticalityLevel, ml.criticality_level)}
                {renderDetail(assetT.fieldEstimatedLifeCycleYears, ml.estimated_life_cycle_years)}
                {renderDateDetail(assetT.fieldLastMaintenanceDate, ml.last_maintenance_date)}
                {renderDateDetail(assetT.fieldNextMaintenanceDue, ml.next_maintenance_due)}
                {renderDetail(assetT.fieldOAndMVendor, ml.o_and_m_vendor)}
              </div>
            </section>
          )}

          {details.data.FinancialOwnership && (
            <section className="asset-section-card asset-section-financial">
              <header className="asset-section-header">
                <div className="asset-section-icon">
                  <IdentificationIcon />
                </div>
                <div className="asset-section-title-block">
                  <h2>{assetT.financialOwnershipTitle}</h2>
                </div>
              </header>

              <div className="asset-details-grid">
                {renderDetail(assetT.fieldSupplierVendor, fo.supplier_vendor)}
                {renderDetail(assetT.fieldDepartmentOwner, fo.department_owner)}
                {renderDetail(
                  assetT.fieldPurchaseCost,
                  fo.purchase_cost ? parseFloat(fo.purchase_cost).toFixed(2) : null
                )}
                {renderDetail(
                  assetT.fieldAnnualOmCost,
                  fo.annual_om_cost ? parseFloat(fo.annual_om_cost).toFixed(2) : null
                )}
              </div>
            </section>
          )}

          {details.data.IotMonitoring && (
            <section className="asset-section-card asset-section-iot">
              <header className="asset-section-header">
                <div className="asset-section-icon">
                  <OperationalIcon />
                </div>
                <div className="asset-section-title-block">
                  <h2>{assetT.iotMonitoringTitle}</h2>
                </div>
              </header>

              <div className="asset-details-grid">
                {renderDetail(assetT.fieldMonitoredParameters, iot.monitored_parameters)}
                {renderDetail(assetT.fieldAlertThresholds, iot.alert_thresholds)}
                {renderDetail(assetT.fieldIntegrationProtocol, iot.integration_protocol)}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};
